import type { FilterFieldType, FilterOperator } from '../../types/contactList'

// =====================================================================
// OPERATOR LABELS (human-readable display text)
// =====================================================================

export const operatorLabels: Record<FilterOperator, string> = {
  Equals: 'is',
  NotEquals: 'is not',
  Contains: 'contains',
  StartsWith: 'starts with',
  EndsWith: 'ends with',
  GreaterThan: '>',
  LessThan: '<',
  GreaterThanOrEqual: '>=',
  LessThanOrEqual: '<=',
  In: 'is one of',
  NotIn: 'is not one of',
  HasTag: 'has tag',
  NotHasTag: 'does not have tag',
  IsNull: 'is empty',
  IsNotNull: 'is not empty',
}

// =====================================================================
// OPERATORS BY FIELD TYPE
// =====================================================================

const textOperators: FilterOperator[] = [
  'Equals', 'NotEquals', 'Contains', 'StartsWith', 'In', 'NotIn', 'IsNull', 'IsNotNull',
]

const scoreOperators: FilterOperator[] = [
  'Equals', 'NotEquals', 'GreaterThanOrEqual', 'LessThanOrEqual', 'GreaterThan', 'LessThan',
]

const booleanOperators: FilterOperator[] = [
  'Equals',
]

const enumOperators: FilterOperator[] = [
  'Equals', 'NotEquals', 'In', 'NotIn',
]

const dateOperators: FilterOperator[] = [
  'GreaterThan', 'LessThan', 'Equals',
]

const tagOperators: FilterOperator[] = [
  'HasTag', 'NotHasTag',
]

export function getOperatorsForFieldType(fieldType: FilterFieldType): FilterOperator[] {
  switch (fieldType) {
    case 'text': return textOperators
    case 'score': return scoreOperators
    case 'boolean': return booleanOperators
    case 'enum': return enumOperators
    case 'date': return dateOperators
    case 'tag': return tagOperators
    default: return textOperators
  }
}

// =====================================================================
// VALUE INPUT TYPE
// =====================================================================

export type ValueInputType = 'text' | 'number' | 'select' | 'date' | 'tag-picker' | 'boolean' | 'multi-select' | 'none'

/**
 * Determines what kind of value input to render based on the field type and operator.
 * Some operators (IsNull, IsNotNull) need no value input.
 * Boolean fields get a Yes/No select.
 */
export function getValueInputType(fieldType: FilterFieldType, operator: FilterOperator): ValueInputType {
  // Null checks never need a value
  if (operator === 'IsNull' || operator === 'IsNotNull') return 'none'

  // In/NotIn always render a multi-select, regardless of field type
  if (operator === 'In' || operator === 'NotIn') return 'multi-select'

  switch (fieldType) {
    case 'text': return 'text'
    case 'score': return 'number'
    case 'boolean': return 'boolean'
    case 'enum': return 'select'
    case 'date': return 'date'
    case 'tag': return 'tag-picker'
    default: return 'text'
  }
}

// =====================================================================
// DATE OPERATOR LABELS (friendlier for date context)
// =====================================================================

export const dateOperatorLabels: Partial<Record<FilterOperator, string>> = {
  GreaterThan: 'is after',
  LessThan: 'is before',
  Equals: 'is',
}
